import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { LgToast, type LgToastTone } from "@noiman/ui/lg";
import { LgRoot, useLgTheme } from "./LgRoot";

export { LgToast, type LgToastProps, type LgToastTone } from "@noiman/ui/lg";

export type LgToastItem = {
  id: string;
  message: ReactNode;
  tone: LgToastTone;
  /** 自動で消えるまでの ms。0 なら押すまで残す。error は既定で残す */
  duration: number;
};

type ShowOptions = { tone?: LgToastTone; duration?: number };
type Api = { show: (message: ReactNode, options?: ShowOptions) => string; dismiss: (id: string) => void };
const ToastContext = createContext<Api | null>(null);

const DEFAULT_DURATION = 5000;

function ToastTimer({ item, onDone }: { item: LgToastItem; onDone: (id: string) => void }) {
  useEffect(() => {
    if (item.duration <= 0) return;
    const t = setTimeout(() => onDone(item.id), item.duration);
    return () => clearTimeout(t);
  }, [item.id, item.duration, onDone]);
  return null;
}

/**
 * 通知の置き場。LgRoot の中に置く。通知は body へ出し、今のテーマと明暗を引き継いだ LgRoot で包む。
 * 右下に重ねて出し、新しいものが下。useLgToast で出す
 */
export function LgToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<LgToastItem[]>([]);
  const [mounted, setMounted] = useState(false);
  const seq = useRef(0);
  const { theme, mode } = useLgTheme();
  useEffect(() => setMounted(true), []);

  const dismiss = useCallback((id: string) => setItems((prev) => prev.filter((i) => i.id !== id)), []);
  const show = useCallback((message: ReactNode, options?: ShowOptions) => {
    seq.current += 1;
    const id = `lg-toast-${seq.current}`;
    const tone = options?.tone ?? "info";
    const duration = options?.duration ?? (tone === "error" ? 0 : DEFAULT_DURATION);
    setItems((prev) => [...prev, { id, message, tone, duration }]);
    return id;
  }, []);
  const api = useMemo(() => ({ show, dismiss }), [show, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {mounted && typeof document !== "undefined"
        ? createPortal(
            <LgRoot theme={theme} mode={mode} className="lg-toast-viewport">
              <div role="region" aria-label="通知" className="lg-toast-stack">
                {items.map((item) => (
                  <LgToast key={item.id} message={item.message} tone={item.tone} onClose={() => dismiss(item.id)} />
                ))}
              </div>
              {items.map((item) => (
                <ToastTimer key={item.id} item={item} onDone={dismiss} />
              ))}
            </LgRoot>,
            document.body,
          )
        : null}
    </ToastContext.Provider>
  );
}

export function useLgToast(): Api {
  const api = useContext(ToastContext);
  if (!api) throw new Error("useLgToast は LgToastProvider の中で使います");
  return api;
}
